import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { FireAlarmHero } from "@/components/FireAlarmHero";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { trackEvent } from "@/hooks/useAnalytics";
import { ArrowRight, ShieldCheck, Flame, BookOpen, Bot, ClipboardCheck, Award, Zap, CheckCircle2, TrendingUp, Building2, Instagram } from "lucide-react";
import { useJsonLd, schoolSchema, courseSchema } from "@/hooks/useJsonLd";

const PANEL_IMG = "https://images.pexels.com/photos/3958649/pexels-photo-3958649.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const INSTALL_IMG = "https://images.pexels.com/photos/12997193/pexels-photo-12997193.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MODULES = [
  { id: 1, title: "Introduction to Fire Alarm Systems", hours: 15, topics: ["Fire Detection Basics", "System Components", "System Architecture"] },
  { id: 2, title: "Code Requirements", hours: 20, topics: ["NFPA 72 Fundamentals", "Installation Standards"] },
  { id: 3, title: "Power Supplies & Circuits", hours: 10, topics: ["Battery Calculations", "NEC 760 & Wiring"] },
  { id: 4, title: "Testing, Inspection & Maintenance", hours: 10, topics: ["Acceptance Testing", "ITM Frequencies"] },
  { id: 5, title: "Final Exam Prep & Practice", hours: 5, topics: ["Exam Strategy", "50-Q Practice Exam"] },
];

const FEATURES = [
  { icon: BookOpen, title: "Full written curriculum", body: "Six modules totaling 70 hours of deeply-researched content aligned to the NICET Level 1 Foundation blueprint and NYC S-98 review." },
  { icon: Bot, title: "AI Tutor on every lesson", body: "Ask questions about NFPA 72, circuits, or siting and get precise, sourced answers powered by Claude Sonnet 4.5." },
  { icon: ClipboardCheck, title: "Quizzes every lesson", body: "Check your knowledge after each lesson and track your best scores as you move through the program." },
  { icon: Award, title: "50-question practice exam", body: "Simulate the real NICET Foundation experience. Pass with 70% to earn your certificate of completion." },
];

export default function Landing() {
  useDocumentMeta({
    title: "NICET Level 1 · 2 · 3 + NYC S-98 Fire Alarm Training",
    description: "140 hours of interactive fire alarm training for NICET candidates. NICET Level 1 Foundation + Level 2 + Level 3 + NYC FDNY S-98, AI tutor, QR-verified certificate. $149 lifetime access.",
    image: "/og/og-home.png",
    path: "/",
  });
  useJsonLd([schoolSchema(), courseSchema()]);
  return (
    <div className="bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200" data-testid="landing-hero">
        <div className="grid-bg absolute inset-0 opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-in">
              <div className="overline mb-5">
                <span className="red-bar" />APM FIRE ALARM EXAM PREP
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-zinc-950 leading-[0.95]">
                Master fire alarm systems.
                <br />
                <span className="text-red-600">Pass NICET Level 1.</span>
              </h1>
              <p className="mt-7 text-lg text-zinc-700 max-w-2xl leading-relaxed">
                An interactive, 140-hour curriculum spanning NICET Level 1 Foundation, Level 2, Level 3,
                and the NYC FDNY S-98 Certificate of Fitness. NFPA 72, UL-listed components, installation
                standards, performance-based design, supervising-station ops, smoke control, and a 50-question
                final practice exam — all in one place.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-enroll-btn" onClick={() => trackEvent("enroll_click", { location: "hero" })}><button className="btn-primary">Enroll now<ArrowRight className="w-4 h-4" /></button></Link>
                <Link to="/sample" data-testid="hero-sample-btn" onClick={() => trackEvent("sample_click", { location: "hero" })}><button className="btn-outline">Preview a free lesson</button></Link>
                <Link to="/nicet-exam-prep" data-testid="hero-nicet-btn"><button className="btn-outline">NICET exam prep</button></Link>
                <a href="#curriculum" data-testid="hero-view-curriculum-btn"><button className="btn-outline">View curriculum</button></a>
              </div>

              <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl">
                {[
                  { k: "140", l: "Total hours" },
                  { k: "15", l: "Modules (L1 · L2 · L3 · S-98)" },
                  { k: "50", l: "Practice exam questions" },
                ].map((s) => (
                  <div key={s.l} className="border-l-2 border-red-600 pl-4">
                    <div className="font-heading text-4xl font-bold tracking-tight">{s.k}</div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 relative fade-in">
              <div className="relative aspect-[4/5] border border-zinc-200 overflow-hidden bg-zinc-50">
                <FireAlarmHero className="w-full h-full" />
                <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-zinc-200 p-5">
                  <div className="overline mb-1">Certificate track</div>
                  <div className="font-heading text-lg font-bold tracking-tight">NICET Level 1 · Foundation</div>
                  <div className="text-xs text-zinc-600 mt-1 font-mono">NFPA 72 · UL 864 · NEC 760</div>
                </div>
              </div>
              <div className="hidden lg:block absolute -left-8 -bottom-8 bg-red-600 text-white p-5 max-w-[240px]">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em]">Why APM</div>
                <div className="font-heading text-xl font-bold mt-1 leading-tight">Field-tested instruction, not fluff.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-zinc-200 py-24 sm:py-28" data-testid="landing-features">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="overline mb-4"><span className="red-bar" />What you get</div>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Built for the way electricians actually learn.</h2>
              <p className="mt-5 text-zinc-600 leading-relaxed">
                Readable technical content, unlimited AI-assisted Q&amp;A, self-grading quizzes, and an exam
                simulator that mirrors the NICET testing environment.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {FEATURES.map((f, i) => (
                <div key={i} className="card-tech p-7" data-testid={`feature-${i}`}>
                  <div className="w-9 h-9 bg-red-50 text-red-600 grid place-items-center mb-4">
                    <f.icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="font-heading text-lg font-bold tracking-tight">{f.title}</h3>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="border-b border-zinc-200 py-24 sm:py-28 bg-zinc-50" data-testid="landing-curriculum">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="overline mb-4"><span className="red-bar" />Curriculum · 70 hours</div>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Six modules. One certificate. One NYC credential.</h2>
            </div>
            <Link to="/register" className="text-sm font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid="curriculum-enroll-link">
              Start learning <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid lg:grid-cols-12 gap-4">
            {MODULES.map((m, idx) => (
              <div key={m.id} className={`card-tech p-8 flex flex-col ${idx === 0 ? "lg:col-span-6" : idx === 1 ? "lg:col-span-6" : "lg:col-span-4"}`} data-testid={`module-card-${m.id}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="font-mono text-xs uppercase tracking-[0.2em] text-red-600">Module {m.id}</div>
                  <div className="font-mono text-xs text-zinc-500">{m.hours} hrs</div>
                </div>
                <h3 className="font-heading text-2xl font-bold tracking-tight mb-3">{m.title}</h3>
                <ul className="mt-auto space-y-1.5 text-sm text-zinc-700">
                  {m.topics.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <Zap className="w-3.5 h-3.5 text-red-600 mt-1 flex-shrink-0" strokeWidth={2.5} />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="border-b border-zinc-200 py-24 sm:py-28" data-testid="landing-audience">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="overline mb-4"><span className="red-bar" />Who this is for</div>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.95]">
                Built for the working trade.<br/><span className="text-red-600">Not the classroom.</span>
              </h2>
              <p className="text-zinc-700 mt-6 max-w-md leading-relaxed">
                If you're already on the wire, in the panel, or on the job site —
                this course meets you where you are and adds the certifications that get you paid.
              </p>
              <Link to="/career" className="mt-8 inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700" data-testid="landing-career-link">
                See the full career roadmap <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-2">
              {[
                "Electricians",
                "Low-Voltage Technicians",
                "Fire Alarm Helpers",
                "Building Engineers",
                "Property Managers",
                "Career Changers",
                "Union Apprentices",
                "FDNY Cert Candidates",
              ].map((label) => (
                <div key={label} className="flex items-center gap-3 p-4 border-2 border-zinc-100 hover:border-red-300 transition-colors" data-testid={`landing-audience-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-sm font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career roadmap + free guide + corporate triple CTA */}
      <section className="border-b border-zinc-200 py-20 bg-zinc-50" data-testid="landing-pathways">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/career" className="card-tech p-6 hover:shadow-md transition-shadow group" data-testid="landing-pathway-career">
              <TrendingUp className="w-6 h-6 text-red-600 mb-3" />
              <div className="font-heading text-xl font-bold tracking-tight mb-1 group-hover:text-red-600 transition-colors">Career roadmap</div>
              <div className="text-sm text-zinc-600 mb-4">Helper → S-95 → S-98 → NICET I-III → Lead Tech. Full salary guide included.</div>
              <span className="text-xs font-mono uppercase tracking-widest text-red-600 inline-flex items-center gap-1">
                See the path <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            <Link to="/free-guide" className="card-tech p-6 hover:shadow-md transition-shadow group bg-red-50 border-red-200" data-testid="landing-pathway-freeguide">
              <BookOpen className="w-6 h-6 text-red-600 mb-3" />
              <div className="font-heading text-xl font-bold tracking-tight mb-1 group-hover:text-red-600 transition-colors">25 free S-98 questions</div>
              <div className="text-sm text-zinc-700 mb-4">Real exam-level practice, no card required. See where you stand in 15 minutes.</div>
              <span className="text-xs font-mono uppercase tracking-widest text-red-600 inline-flex items-center gap-1">
                Try it free <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            <Link to="/corporate" className="card-tech p-6 hover:shadow-md transition-shadow group" data-testid="landing-pathway-corporate">
              <Building2 className="w-6 h-6 text-red-600 mb-3" />
              <div className="font-heading text-xl font-bold tracking-tight mb-1 group-hover:text-red-600 transition-colors">Train your team</div>
              <div className="text-sm text-zinc-600 mb-4">Workforce training for contractors and engineering firms. 5-500 person teams.</div>
              <span className="text-xs font-mono uppercase tracking-widest text-red-600 inline-flex items-center gap-1">
                Corporate options <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>

          {/* Additional free PDF resources — quick strip */}
          <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="landing-extra-magnets">
            <Link to="/cheat-sheet" className="card-tech p-5 hover:shadow-md transition-shadow group flex items-start gap-3" data-testid="landing-pathway-cheat-sheet">
              <ClipboardCheck className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-heading text-base font-bold tracking-tight mb-0.5 group-hover:text-red-600 transition-colors">S-98 Exam Cheat Sheet (PDF)</div>
                <div className="text-xs text-zinc-600">10 sections · NFPA 72 vs NYC FC overrides · signal hierarchy · exam-day tactics</div>
              </div>
            </Link>
            <Link to="/top-50-questions" className="card-tech p-5 hover:shadow-md transition-shadow group flex items-start gap-3" data-testid="landing-pathway-top-50">
              <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-heading text-base font-bold tracking-tight mb-0.5 group-hover:text-red-600 transition-colors">Top 50 FDNY S-98 Questions (PDF)</div>
                <div className="text-xs text-zinc-600">50 practice Qs · NFPA citation on every answer · score 70%+ before exam day</div>
              </div>
            </Link>
          </div>
        </div>
      </section>
      <section id="nicet" className="border-b border-zinc-200 py-24 sm:py-28" data-testid="landing-nicet">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <div className="overline mb-4"><span className="red-bar" />Certification path</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Why NICET Level 1 matters.</h2>
            <p className="mt-5 text-zinc-700 leading-relaxed">
              NICET certification is the gold-standard credential for fire alarm technicians in North America. The
              Level 1 Foundation exam validates your working knowledge of NFPA 72, system components, and field
              conditions — unlocking higher-paying installation and service roles from day one.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-5 max-w-md">
              <div className="border-l-2 border-red-600 pl-4">
                <div className="font-heading text-3xl font-bold">~2 hr</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Exam length</div>
              </div>
              <div className="border-l-2 border-red-600 pl-4">
                <div className="font-heading text-3xl font-bold">70%</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Passing score</div>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2 grid grid-cols-2 gap-3">
            <div className="aspect-[3/4] border border-zinc-200 overflow-hidden">
              <img
                src={PANEL_IMG}
                alt="NFPA 72 fire alarm control panel — APM Fire Alarm Exam Prep curriculum"
                width="940" height="650"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[3/4] border border-zinc-200 overflow-hidden mt-10">
              <img
                src={INSTALL_IMG}
                alt="Fire alarm device field installation — wiring devices, manual pull stations, and notification appliances"
                width="940" height="650"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meet your instructor */}
      <section className="border-b border-zinc-200 py-24 sm:py-28 bg-zinc-50" data-testid="landing-instructor">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <div className="aspect-square bg-zinc-950 border-4 border-red-600 grid place-items-center max-w-sm">
                <div className="text-center px-6">
                  <div className="w-20 h-20 rounded-full bg-red-600 grid place-items-center mx-auto mb-3">
                    <span className="font-heading text-3xl font-bold tracking-tighter text-white">TM</span>
                  </div>
                  <div className="font-heading text-xl font-bold tracking-tight text-white">Trevor Marson</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">Lead Instructor · IBEW Local 3</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="overline mb-3"><span className="red-bar" />Meet your instructor</div>
              <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-4">
                Taught by someone who's been on the wire.
              </h2>
              <p className="text-zinc-700 leading-relaxed mb-4 max-w-2xl">
                <strong>Trevor Marson</strong> has 15+ years of NYC fire-alarm field experience —
                installations, FDNY-witnessed inspections, and service in some of the most demanding
                buildings in America: World Trade Center, Hudson Yards, JPMorgan Chase, Federal Reserve
                Bank of NY, JFK International Airport, NYU, John Jay College.
              </p>
              <p className="text-zinc-700 leading-relaxed mb-5 max-w-2xl">
                He holds FDNY <strong>S-95, S-98, S-13, and F-53</strong> Certs of Fitness plus
                OSHA 30, and is a proud member of <strong>IBEW Local 3 (H Division)</strong>.
                Every lesson here is grounded in real installs, real inspections, and real
                troubleshooting from the field.
              </p>
              <Link to="/about-instructor" data-testid="landing-instructor-link">
                <button className="btn-outline">Read full bio <ArrowRight className="w-4 h-4" /></button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-zinc-950 text-white" data-testid="landing-cta">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-4 text-red-500"><span className="red-bar" />Start today</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
              Your NICET certificate starts with one lesson.
            </h2>
          </div>
          <div className="flex gap-3">
            <Link to="/register" data-testid="cta-enroll-btn">
              <button className="btn-primary">Create free account <ArrowRight className="w-4 h-4" /></button>
            </Link>
            <Link to="/login" data-testid="cta-login-btn">
              <button className="bg-transparent border border-white/50 text-white px-6 py-3 font-semibold hover:bg-white/10 transition-colors rounded-sm">Log in</button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-3 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-red-600" />
            <span className="font-semibold text-zinc-800">APM Fire Alarm Exam Prep</span>
            <span className="font-mono text-xs">· © 2026</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/apmfireexam_prep"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-zinc-600 hover:text-red-600 transition-colors"
              aria-label="Follow APM Fire Alarm Exam Prep on Instagram"
              data-testid="footer-instagram"
            >
              <Instagram className="w-4 h-4" />
              <span className="font-mono text-xs uppercase tracking-widest">@apmfireexam_prep</span>
            </a>
            <div className="font-mono text-xs uppercase tracking-widest hidden sm:block">Built for future NICET technicians</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
